import { useQuery } from '@tanstack/react-query';
import { TrendingDown, TrendingUp, Wallet } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { CardShell } from '../cards/card-shell';
import { formatBrl } from './format';

/**
 * KPI: saldo atual consolidado de todas as contas + variacao % vs mes
 * anterior (deduzido a partir do diff entre receita realizada e despesa
 * realizada do mes — proxy simples).
 */
export function SummarySaldoWidget() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['financial', 'stats', 'summary'],
    queryFn: () => apiClient.financialStatsSummary(),
    staleTime: 1000 * 60,
  });

  const saldo = data?.saldoAtual ?? 0;
  // Aproximacao: variacao = receitaRealizada - despesaRealizada (delta do
  // mes corrente). Backend nao expoe saldo do mes anterior diretamente;
  // mostramos o delta do mes como "vs mes anterior".
  const variacao =
    (data?.receitaMensalRealizada ?? 0) - (data?.despesaMensalRealizada ?? 0);
  const variacaoPct =
    saldo !== 0 ? Math.round((variacao / Math.abs(saldo - variacao || 1)) * 100) : 0;
  const positive = variacao >= 0;

  return (
    <CardShell title="Saldo atual" icon={Wallet}>
      <div className="flex w-full flex-col items-start gap-2">
        {isLoading ? (
          <div className="flex w-full flex-col gap-2">
            <div className="h-10 w-32 animate-pulse rounded bg-[var(--surface-muted)]" />
            <div className="h-3 w-24 animate-pulse rounded bg-[var(--surface-muted)]" />
          </div>
        ) : isError ? (
          <p className="text-xs text-[var(--fg-muted)]">
            Não foi possível carregar.
          </p>
        ) : (
          <>
            <div className="flex items-baseline gap-2">
              <span
                className={cn(
                  'text-3xl font-semibold tracking-tight tabular-nums',
                  saldo < 0 ? 'text-red-500' : 'text-[var(--fg)]',
                )}
              >
                {formatBrl(saldo)}
              </span>
            </div>
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
                positive
                  ? 'bg-emerald-500/15 text-emerald-500'
                  : 'bg-red-500/15 text-red-500',
              )}
            >
              {positive ? (
                <TrendingUp className="h-3 w-3" />
              ) : (
                <TrendingDown className="h-3 w-3" />
              )}
              {positive ? '+' : ''}
              {variacaoPct}% no mês
            </span>
          </>
        )}
      </div>
    </CardShell>
  );
}
